import React, { useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import { COLORS } from "./constants";
import { panelSx, sectionSubtleSx, sectionTitleSx } from "./styles";

// ─────────────────────────────────────────────────────────────────────────────
// SignalDiagnosticsPanel
//
// Output bitrate over the last couple of minutes, plus frame rate and relay
// speed, each with a plain-language reading ("Keeping up") next to the number.
//
// The chart uses the samples Home/index.jsx already keeps (one per 3 s status
// poll, up to 40), so nothing new is asked of the server. The old version drew
// progress bars against arbitrary maximums (34 fps looked "half bad" against 60).
//
// The backend sends `metrics: null` when nothing is being measured. That is
// shown as "—" and an empty chart message, never as zeros.
// ─────────────────────────────────────────────────────────────────────────────

const DASH = "—";
const POLL_SECONDS = 3;

const niceMax = (v) => {
  if (v <= 1) return 1;
  if (v <= 2) return 2;
  if (v <= 4) return 4;
  if (v <= 6) return 6;
  if (v <= 8) return 8;
  return Math.ceil(v / 5) * 5;
};

function Reading({ label, value, unit, tone, note }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>{label}</Typography>
      <Typography sx={{ mt: 0.25, color: COLORS.text, fontWeight: 600, fontSize: 20, lineHeight: 1.2, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
        {value}
        {value !== DASH && <Box component="span" sx={{ ml: 0.5, fontSize: 12, fontWeight: 500, color: COLORS.muted }}>{unit}</Box>}
      </Typography>
      {/* Colour on the dot only; the words carry the meaning. */}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: tone, flexShrink: 0 }} />
        <Typography noWrap sx={{ color: COLORS.textSoft, fontSize: 12 }}>{note}</Typography>
      </Stack>
    </Box>
  );
}

export function SignalDiagnosticsPanel({ metrics, bitrateHistory = [] }) {
  const [hover, setHover] = useState(null);

  const has = metrics && typeof metrics === "object";
  const fps     = has ? metrics.fps     : null;
  const speed   = has ? metrics.speed   : null;
  const dropped = has ? metrics.dropped : 0;
  const sources = has ? metrics.sources : 0;

  const series = bitrateHistory.filter((v) => typeof v === "number" && !Number.isNaN(v)).map((v) => v / 1000);
  const n = series.length;
  const hasChart = n > 1;
  const yMax = hasChart ? niceMax(Math.max(...series) * 1.15) : 6;
  const y = (v) => 100 - (v / yMax) * 100;
  const x = (i) => (i / (n - 1)) * 300;
  const line = hasChart ? series.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(2)} ${y(v).toFixed(2)}`).join(" ") : "";
  const area = hasChart ? `${line} L300 100 L0 100 Z` : "";
  const avg = hasChart ? series.reduce((a, b) => a + b, 0) / n : null;

  const speedReading = speed == null
    ? { tone: COLORS.muted, note: "No signal" }
    : speed < 0.95 ? { tone: COLORS.red,   note: "Falling behind" }
    : speed < 0.99 ? { tone: COLORS.amber, note: "Slightly behind" }
    : { tone: COLORS.green, note: "Keeping up" };

  const onMove = (e) => {
    if (!hasChart) return;
    const r = e.currentTarget.getBoundingClientRect();
    const i = Math.round(((e.clientX - r.left) / r.width) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };
  const hv = hover != null && hover < n ? hover : null;

  return (
    <Box sx={panelSx}>
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" mb={2.5}>
        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
          <Box sx={{
            width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
            bgcolor: "rgba(43,189,175,0.12)", border: "1px solid rgba(43,189,175,0.35)",
            display: "flex", alignItems: "center", justifyContent: "center"
          }}>
            <TroubleshootIcon sx={{ color: COLORS.cyan, fontSize: 19 }} />
          </Box>
          <Box minWidth={0}>
            <Typography sx={sectionTitleSx}>Signal health</Typography>
            <Typography sx={sectionSubtleSx}>
              {has
                ? `Bitrate out, measured by FFmpeg across ${sources} destination${sources === 1 ? "" : "s"}.`
                : "No measurements. Nothing is being sent right now."}
            </Typography>
          </Box>
        </Stack>
        <Typography sx={{ color: COLORS.text, fontWeight: 600, fontSize: 15, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
          {has && metrics.bitrate != null ? (metrics.bitrate / 1000).toFixed(2) : DASH}
          <Box component="span" sx={{ ml: 0.5, fontSize: 12, fontWeight: 500, color: COLORS.muted }}>Mbps now</Box>
        </Typography>
      </Stack>

      {/* Chart: y labels | plot. One series, so no legend; the title names it. */}
      <Stack direction="row" spacing={1.25}>
        <Stack justifyContent="space-between" sx={{
          height: 150, width: 20, flexShrink: 0, textAlign: "right",
          color: COLORS.muted, fontSize: 11, fontFamily: COLORS.fontMono
        }} aria-hidden="true">
          <span>{yMax}</span><span>{yMax / 2}</span><span>0</span>
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            onMouseMove={onMove} onMouseLeave={() => setHover(null)}
            sx={{ position: "relative", height: 150, "& path, & line": { vectorEffect: "non-scaling-stroke" } }}
          >
            <svg viewBox="0 0 300 100" preserveAspectRatio="none" width="100%" height="150"
              role="img"
              aria-label={hasChart
                ? `Output bitrate over the last ${(n - 1) * POLL_SECONDS} seconds, averaging ${avg.toFixed(2)} Mbps`
                : "No bitrate data yet"}
              style={{ display: "block", overflow: "visible" }}>
              <line x1="0" y1="0" x2="300" y2="0" stroke={COLORS.border} strokeWidth="1" />
              <line x1="0" y1="50" x2="300" y2="50" stroke={COLORS.border} strokeWidth="1" />
              <line x1="0" y1="100" x2="300" y2="100" stroke={COLORS.borderStrong} strokeWidth="1" />
              {hasChart && <path d={area} fill="rgba(43,189,175,0.10)" stroke="none" />}
              {hasChart && <path d={line} fill="none" stroke={COLORS.cyan} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
            </svg>

            {!hasChart && (
              <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", px: 2, textAlign: "center" }}>
                <Typography sx={{ color: COLORS.muted, fontSize: 13 }}>
                  The chart starts a few seconds after video begins flowing.
                </Typography>
              </Box>
            )}

            {hv != null && (
              <>
                <Box sx={{ position: "absolute", top: 0, bottom: 0, left: `${(hv / (n - 1)) * 100}%`, width: "1px", bgcolor: COLORS.borderStrong, pointerEvents: "none" }} />
                <Box sx={{
                  position: "absolute", left: `${(hv / (n - 1)) * 100}%`, top: `${y(series[hv])}%`,
                  width: 10, height: 10, ml: "-5px", mt: "-5px", borderRadius: "50%",
                  bgcolor: COLORS.cyan, boxShadow: `0 0 0 2px ${COLORS.panel}`, pointerEvents: "none"
                }} />
                <Box sx={{
                  position: "absolute", top: -6, left: `${(hv / (n - 1)) * 100}%`,
                  transform: hv > n * 0.65 ? "translate(calc(-100% - 12px), -100%)" : "translate(12px, -100%)",
                  px: 1.25, py: 0.75, borderRadius: "10px", bgcolor: COLORS.raised,
                  border: `1px solid ${COLORS.borderStrong}`, boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                  whiteSpace: "nowrap", pointerEvents: "none"
                }}>
                  <Typography sx={{ color: COLORS.text, fontWeight: 600, fontSize: 14, fontVariantNumeric: "tabular-nums" }}>
                    {series[hv].toFixed(2)} Mbps
                  </Typography>
                  <Typography sx={{ color: COLORS.muted, fontSize: 12 }}>
                    {hv === n - 1 ? "now" : `${(n - 1 - hv) * POLL_SECONDS} s ago`}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1, color: COLORS.muted, fontSize: 11, fontFamily: COLORS.fontMono }} aria-hidden="true">
            <span>{hasChart ? `−${(n - 1) * POLL_SECONDS} s` : ""}</span>
            <span>now</span>
          </Stack>
        </Box>
      </Stack>

      {/* Readings */}
      <Box sx={{
        mt: 2.5, pt: 2.5, borderTop: `1px solid ${COLORS.border}`,
        display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 2
      }}>
        <Reading label="Frame rate" unit="fps"
          value={fps == null ? DASH : String(fps)}
          tone={fps == null ? COLORS.muted : COLORS.green}
          note={fps == null ? "No signal" : "Arriving"} />
        <Reading label="Relay speed" unit="×"
          value={speed == null ? DASH : speed.toFixed(2)}
          tone={speedReading.tone} note={speedReading.note} />
        <Reading label="Average" unit="Mbps"
          value={avg == null ? DASH : avg.toFixed(2)}
          tone={avg == null ? COLORS.muted : COLORS.green}
          note={avg == null ? "No signal" : `Last ${(n - 1) * POLL_SECONDS} s`} />
      </Box>

      {dropped > 0 && (
        <Typography sx={{ mt: 2, color: COLORS.amber, fontSize: 13 }}>
          {dropped} frame{dropped === 1 ? "" : "s"} dropped since the stream started.
        </Typography>
      )}
    </Box>
  );
}
